
import { supabase } from "@/integrations/supabase/client";
import { Reagent } from "@/types/reagent.types";
import { createLogger } from "@/services/utils/loggingUtils";

const logger = createLogger("fetchReagentById");

export const fetchReagentById = async (id: string): Promise<Reagent | null> => {
  try {
    logger.info(`Fetching reagent with id: ${id}`);
    
    const { data, error } = await supabase
      .from("reagents")
      .select(`
        *,
        profiles:user_id (
          id,
          username,
          display_username,
          lab,
          university,
          city,
          country
        ),
        categories:category_id (
          id,
          name
        )
      `)
      .eq("id", id)
      .single();

    if (error) {
      logger.error("Error fetching reagent:", error);
      return null;
    }

    if (!data) {
      logger.info("Reagent not found");
      return null;
    }

    // Map the data to our Reagent type with profile information
    const reagent: Reagent = {
      ...data,
      // Standard fields
      id: data.id,
      name: data.name,
      catalogNumber: data.catalogue_number || "",
      category: data.categories?.name || "",
      description: data.description || "",
      amount: `${data.amount || ""} ${data.amount_unit || ""}`.trim(),
      storageConditions: data.storage_conditions || "",
      imageUrl: data.image_url || "",
      dateAdded: data.created_at || "",
      numberOfPackages: data.number_of_packages ? data.number_of_packages.toString() : "",
      expiryDate: data.expiry_date || null,
      
      // User profile related data
      userName: data.profiles?.display_username || data.profiles?.username || "",
      labName: data.profiles?.lab || "",
      university: data.profiles?.university || "",
      city: data.profiles?.city || "",
      country: data.profiles?.country || "",
      
      // Include profile details for direct access
      profiles: data.profiles ? {
        id: data.profiles.id,
        username: data.profiles.username || "",
        display_username: data.profiles.display_username || "",
        lab: data.profiles.lab || "",
        university: data.profiles.university || "",
        city: data.profiles.city || "",
        country: data.profiles.country || ""
      } : undefined
    };

    logger.info("Successfully fetched reagent");
    return reagent;
  } catch (error) {
    logger.error("Exception fetching reagent:", error);
    return null;
  }
};
